import { type Capabilities } from '@canvas/capability';
import { type PermissionsManager } from '@canvas/permissions/manager';
import { appPermissions } from '@canvas/permissions/singleton';
import { PolicyManager } from '@canvas/policies';
import { PolicyManagerExternalApi } from '@canvas/policies/api';
import { policyAction, type PolicyAction } from '@canvas/policies/types';

import { type AbilityInput } from './abilities-types';
import {
  type CapabilityOverride,
  CapabilityOverridesApi,
  mergeCapabilityOverride,
} from './capability-overrides';

type Subscriber = VoidFunction;

export class AbilitiesManager {
  /**
   * Subscribers to notify when there are any changes in claims granted to a user.
   */
  #subscribers: Subscriber[] = [];

  /**
   * Primary store which answers whether a user has the ability to perform a
   * chosen action. Enforces the current set of policies in the application. See
   * packages/policies/README.md and packages/abilities/README.md for more.
   */
  #policyManager: PolicyManager = new PolicyManager();
  /**
   * External API for interacting with policies - for adding and
   * removing them. {@link #policyManager} SHOULD NOT be exposed, ever.
   */
  #policyManagerApi = new PolicyManagerExternalApi(
    this.#policyManager,
    this.notifySubscribers.bind(this)
  );

  /**
   * Eventual goal is to move capabilities as policies, lets store it here for now
   */
  #capabilities?: Capabilities;

  #capabilityOverrides = new Map<string, CapabilityOverride>();
  #effectiveCapabilities?: Capabilities;
  #capabilityOverridesApi = new CapabilityOverridesApi(
    this.#capabilityOverrides,
    this.#onCapabilityOverridesChange.bind(this)
  );

  /**
   * Manages permissions the current user has access to. Can be accessed through
   * the top-level {@link permissions} getter. Responsible for maintaining which
   * claims a user has been granted based upon the board token.
   */
  #permissionsManager: PermissionsManager;

  public constructor(permissionsManager = appPermissions) {
    this.#permissionsManager = permissionsManager;
  }

  get policies() {
    return this.#policyManagerApi;
  }

  get permissions() {
    return this.#permissionsManager;
  }

  get capabilityOverrides() {
    return this.#capabilityOverridesApi;
  }

  #recomputeEffectiveCapabilities(): void {
    const base = this.#capabilities;
    if (this.#capabilityOverrides.size === 0) {
      this.#effectiveCapabilities = base;
      return;
    }
    this.#effectiveCapabilities = Array.from(this.#capabilityOverrides.values()).reduce(
      (acc, override) => mergeCapabilityOverride(acc, override),
      base
    );
  }

  #onCapabilityOverridesChange(): void {
    this.#recomputeEffectiveCapabilities();
    this.notifySubscribers();
  }

  get capabilities(): Capabilities | undefined {
    return this.#effectiveCapabilities;
  }

  set capabilities(capabilities: Capabilities) {
    this.#capabilities = capabilities;
    this.#recomputeEffectiveCapabilities();
  }

  /**
   * Gets capabilities for a specific key in a type-safe manner
   * @param key - The key to look up in the capabilities object
   * @returns The capabilities object for the given key, or undefined if not found
   */
  getCapabilities<T>(key: string): T | undefined {
    const effective = this.#effectiveCapabilities;
    if (!effective) {
      return undefined;
    }
    return effective[key] as T | undefined;
  }

  /**
   * Checks if a policy is currently active
   * @param policy
   * @returns
   */
  has(policy: string) {
    return this.#policyManager.hasNamespace(policy);
  }

  /**
   * Subscribe to changes in abilities a user has.
   *
   * @description Can change based upon any change in permissions
   * @description Can change based upon any change in policies
   *
   * @param subscriber
   * @returns
   */
  subscribe(subscriber: Subscriber) {
    this.#subscribers.push(subscriber);

    // subscribe to changes in permissions as well
    const unsubscribeFromPermissionChanges =
      this.#permissionsManager.subscribe(subscriber);

    return () => {
      unsubscribeFromPermissionChanges();
      this.unsubscribe(subscriber);
    };
  }

  /**
   * Checks if a user has been granted an ability based upon the policies
   * currently active in the application.
   * @param input
   * @returns
   */
  can(input: AbilityInput) {
    return this.#policyManager.can(
      policyAction(input),
      this.permissions.claims as Set<PolicyAction>
    );
  }

  /**
   * Checks if a user HAS NOT been granted an ability. Inverse of {@link can}.
   * @param input
   * @returns
   */
  cannot(input: AbilityInput) {
    return this.#policyManager.cannot(
      policyAction(input),
      this.permissions.claims as Set<PolicyAction>
    );
  }

  reset() {
    this.notifySubscribers();
  }

  destroy() {
    this.#subscribers.length = 0;

    this.#policyManager.destroy();
    this.#policyManagerApi.destroy();
    this.#capabilityOverrides.clear();
    this.#capabilities = undefined;
    this.#effectiveCapabilities = undefined;
  }

  private unsubscribe(subscriber: Subscriber) {
    const idx = this.#subscribers.findIndex((s) => s === subscriber);
    if (idx === -1) {
      return;
    }

    this.#subscribers.splice(idx, 1);
  }

  private notifySubscribers() {
    this.#subscribers.forEach((subscriber) => subscriber());
  }
}
